import BuncherDetailDialog from "./BuncherDetailDialog";
import FarmGradeoutDialog from "./FarmGradeoutDialog";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../../api/api";
import "../../styles/LiveBuncherPerformance.css";
import "../../styles/HarvestBoard.css";

const publicApi = axios.create({ baseURL: api.defaults.baseURL, timeout: 8000 });
const number = value => value == null ? "—" : value.toLocaleString();
const BUNCHING_WINDOW_SECONDS = 8 * 60 * 60 + 40 * 60;
const nairobiToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Nairobi", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const shiftDay = (day, amount) => { const [y, m, d] = day.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d + amount)).toISOString().slice(0, 10); };
const hhmm = date => date.toLocaleTimeString("en-GB", { timeZone: "Africa/Nairobi", hour: "2-digit", minute: "2-digit" });

// Big screens get a scale-to-fit board; phones, tablets and short windows get a normal scrolling page.
const wantsFit = () => window.innerWidth >= 1000 && window.innerHeight >= 600;

/* Flower used as the brand mark and as the progress bud on every row. */
const PETAL_ANGLES = [0, 72, 144, 216, 288];
const Flower = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    {PETAL_ANGLES.map(a => (
      <g key={a} transform={`rotate(${a} 12 12)`}><ellipse className="hv-petal" cx="12" cy="6.2" rx="3.6" ry="5.2" /></g>
    ))}
    <circle className="hv-core" cx="12" cy="12" r="3" />
  </svg>
);

export default function LiveBuncherPerformance() {
  const [historyDate, setHistoryDate] = useState(() => shiftDay(nairobiToday(), -1));
  const [historyResult, setHistoryResult] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const isHistorical = Boolean(historyResult);
  const [detail, setDetail] = useState(null);
  const [showGradeout, setShowGradeout] = useState(false);
  const closeDetail = useCallback(() => setDetail(null), []);
  const [data, setData] = useState(null);
  const [farm, setFarm] = useState(() => new URLSearchParams(window.location.search).get("farm") || "");
  const [offline, setOffline] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [notice, setNotice] = useState("");
  const [mode, setMode] = useState(() => wantsFit() ? "fit" : "flow");
  const surface = useRef(null);
  const canvas = useRef(null);
  const [fit, setFit] = useState({ scale: 1, columns: 3 });

  /* ---------- live polling (unchanged) ---------- */
  useEffect(() => {
    if (historyResult || historyLoading) return undefined;
    let stopped = false, busy = false;
    const controller = new AbortController();
    const refresh = async () => {
      if (busy) return;
      busy = true;
      try {
        const response = await publicApi.get("core/live-bunchers/", { signal: controller.signal });
        if (!stopped) { setData(response.data); setOffline(false); }
      } catch { if (!stopped) setOffline(true); }
      finally { busy = false; }
    };
    refresh();
    const timer = setInterval(refresh, 1000);
    return () => { stopped = true; clearInterval(timer); controller.abort(); };
  }, [historyResult, historyLoading]);

  const displayData = historyResult || data;
  const selected = displayData?.farms.find(f => f.name === farm) || displayData?.farms[0];
  const rows = selected?.bunchers || [];

  const detailProps = person => isHistorical ? {} : ({
    role: "button", tabIndex: 0, "aria-haspopup": "dialog",
    onClick: () => setDetail({ person, farm: selected.name }),
    onKeyDown: event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setDetail({ person, farm: selected.name }); } },
  });
  const clickable = isHistorical ? "" : "is-click";

  /* ---------- layout: fit-to-screen OR flowing page (unchanged) ---------- */
  useLayoutEffect(() => {
    const host = surface.current, content = canvas.current;
    if (!host || !content) return;
    let frame;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const nextMode = wantsFit() ? "fit" : "flow";
        setMode(prev => prev === nextMode ? prev : nextMode);
        const vw = host.clientWidth, vh = host.clientHeight;
        if (!vw || !vh) return;
        const n = rows.length || 1;

        if (nextMode === "flow") {
          content.style.width = ""; content.style.minHeight = "";
          const columns = vw < 700 ? 1 : vw < 1100 ? 2 : 3;
          setFit(prev => prev.columns === columns && prev.scale === 1 ? prev : { scale: 1, columns });
          return;
        }

        const aspect = vw / vh;
        const standard = Math.max(1, Math.min(n, Math.ceil(Math.sqrt(n * aspect / 4))));
        const columns = vw >= 1100 && vh >= 620
          ? Math.max(standard, Math.min(n, Math.ceil(Math.sqrt(n * aspect / 2))))
          : standard;
        const maxScale = Math.min(4, vw / 1000);
        const fits = s => {
          content.style.width = `${vw / s}px`;
          content.style.minHeight = "0px";
          return content.scrollHeight * s <= vh + 0.5;
        };
        let lo = 0.12, hi = Math.max(maxScale, 0.12), scale;
        if (fits(hi)) scale = hi;
        else {
          for (let i = 0; i < 16; i++) { const mid = (lo + hi) / 2; if (fits(mid)) lo = mid; else hi = mid; }
          scale = lo;
        }
        content.style.width = `${vw / scale}px`;
        content.style.minHeight = `${vh / scale}px`;
        setFit(prev => prev.columns === columns && Math.abs(prev.scale - scale) < 0.0001 ? prev : { scale, columns });
      });
    };
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(host); observer?.observe(content);
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    window.visualViewport?.addEventListener("resize", measure);
    document.fonts?.ready.then(measure);
    measure();
    return () => {
      observer?.disconnect(); cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, [rows.length, fullscreen, notice, offline, selected?.name, mode, isHistorical]);

  /* ---------- fullscreen (unchanged) ---------- */
  useEffect(() => {
    const changed = () => setFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", changed);
    document.addEventListener("webkitfullscreenchange", changed);
    return () => { document.removeEventListener("fullscreenchange", changed); document.removeEventListener("webkitfullscreenchange", changed); };
  }, []);
  const fullscreenSupported = typeof document !== "undefined" && Boolean(document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen);
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (document.webkitFullscreenElement) await document.webkitExitFullscreen();
      else if (surface.current.requestFullscreen) await surface.current.requestFullscreen();
      else if (surface.current.webkitRequestFullscreen) await surface.current.webkitRequestFullscreen();
      else setNotice("Full screen is unavailable in this browser. Use your browser's full-screen or Add to Home Screen option.");
    } catch { setNotice("Full screen could not open. Try your browser's full-screen option."); }
  };

  /* ---------- farm + history (unchanged) ---------- */
  const pick = name => {
    setHistoryResult(null); setHistoryError(""); setFarm(name);
    const url = new URL(window.location.href); url.searchParams.set("farm", name); window.history.replaceState(null, "", url);
  };
  const loadHistory = async () => {
    if (!selected?.name || !historyDate || historyDate >= nairobiToday()) { setHistoryError("Choose one completed day before today."); return; }
    setHistoryLoading(true); setHistoryError("");
    try {
      const response = await publicApi.get("core/live-bunchers/history/", { params: { farm: selected.name, day: historyDate }, timeout: 20000 });
      setHistoryResult(response.data);
    } catch (error) {
      const payload = error.response?.data;
      setHistoryError(payload?.day?.[0] || payload?.detail || "Could not load that day. Please try again.");
    } finally { setHistoryLoading(false); }
  };
  const returnToLive = () => { setHistoryResult(null); setHistoryError(""); };

  /* ---------- derived numbers (unchanged logic) ---------- */
  const windowStems = selected?.window_stems === undefined ? selected?.total_stems : selected.window_stems;
  const teamPercent = selected?.packhouse_target > 0 ? Math.floor(windowStems / selected.packhouse_target * 100) : null;
  const windowRows = rows.filter(p => p.has_window_capture ?? p.stems > 0);
  const windowBuncherCount = selected?.bunchers_in_window ?? windowRows.length;
  const target = selected?.target_per_person;
  const finishers = target ? windowRows.filter(p => p.stems >= target).length : 0;
  const podium = windowRows.slice(0, 3);
  const scale = Math.max(target || 0, ...rows.map(r => r.stems), 1);
  const windowStart = selected?.start_time ? new Date(selected.start_time) : null;
  const windowEnd = windowStart
    ? new Date(windowStart.getTime() + BUNCHING_WINDOW_SECONDS * 1000)
    : selected?.window_end ? new Date(selected.window_end) : null;
  const windowRemaining = windowEnd ? Math.max(0, Math.floor((windowEnd.getTime() - Date.now()) / 1000)) : null;
  const windowElapsed = windowRemaining === 0;
  const windowActive = !isHistorical && Boolean(windowStart && windowRemaining > 0 && Date.now() >= windowStart.getTime());
  const elapsedPct = windowRemaining == null ? 0 : Math.min(100, Math.max(0, (1 - windowRemaining / BUNCHING_WINDOW_SECONDS) * 100));
  const clock = seconds => {
    if (seconds == null) return "Awaiting start";
    if (seconds <= 0) return "Complete";
    const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };
  const targetNote = selected?.excess > 0 ? `+${number(selected.excess)} excess`
    : selected?.deficit > 0 ? `${number(selected.deficit)} deficit`
    : selected?.packhouse_target == null ? (target ? "No window bunchers yet" : "Target not set")
    : "Target met";

  const statusText = isHistorical ? "Viewing a past day" : offline ? "Connection lost, retrying" : data ? "Live, updates every second" : "Connecting…";
  const statusClass = isHistorical ? "is-history" : offline ? "is-stale" : "";

  return (
    <main className={`hv-board is-${mode}`} ref={surface}>
      <div className="hv-canvas" ref={canvas} style={mode === "fit" ? { transform: `scale(${fit.scale})` } : undefined}>

        {/* ===== header ===== */}
        <header className="hv-header">
          <div className="hv-brand">
            <span className="hv-logo"><Flower /></span>
            <div>
              <h1>Buncher live performance</h1>
              <p className="hv-sub">{displayData?.day || "Today"}, Nairobi time. Targets assume 7.67 capture hours.</p>
            </div>
          </div>

          <nav className="hv-nav" aria-label="Farm and day selection">
            <div className="hv-tabs" role="group" aria-label="Farm">
              {data?.farms.map(f => (
                <button key={f.name} className="hv-tab" aria-pressed={selected?.name === f.name} onClick={() => pick(f.name)} disabled={historyLoading}>{f.name}</button>
              ))}
            </div>
            <div className={`hv-history ${isHistorical ? "is-active" : ""}`}>
              <label className="hv-history-label" htmlFor="hv-history-date">
                <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5" width="17" height="16" rx="3" /><path d="M7.5 3v4M16.5 3v4M4 9h16M8 13h3M8 17h2" /></svg>
                Past day
              </label>
              <input id="hv-history-date" type="date" value={historyDate} max={shiftDay(data?.day || nairobiToday(), -1)} onChange={e => setHistoryDate(e.target.value)} disabled={historyLoading} />
              <button type="button" className="hv-btn hv-btn-primary" onClick={loadHistory} disabled={!selected || historyLoading}>{historyLoading ? "Loading…" : "View day"}</button>
              {isHistorical && <button type="button" className="hv-btn hv-btn-live" onClick={returnToLive} disabled={historyLoading}>Back to live</button>}
            </div>
          </nav>

          <div className="hv-controls">
            <span className={`hv-status ${statusClass}`} role="status">{statusText}</span>
            {fullscreenSupported && <button type="button" className="hv-btn" onClick={toggleFullscreen}>{fullscreen ? "Exit full screen" : "Full screen"}</button>}
          </div>
        </header>

        {historyError && <p role="alert" className="hv-notice">{historyError}</p>}
        {notice && <p role="status" className="hv-notice">{notice}<button onClick={() => setNotice("")}>Dismiss</button></p>}

        {/* ===== arena: leader / chasers / team ring ===== */}
        {windowRows.length > 0 && (
          <section className="hv-arena" aria-label="Leading bunchers">
            <div className={`hv-hero ${clickable}`} {...detailProps(podium[0])}>
              <div className="hv-crown">
                <svg viewBox="0 0 40 32" aria-hidden="true"><path d="M5 24 2 8l10 7L20 3l8 12 10-7-3 16Z" fill="currentColor" /><path d="M6 29h28" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
              </div>
              <div className="hv-hero-text">
                <p>In the lead</p>
                <h2>{podium[0].name}</h2>
                <span>{podium[1] ? (podium[0].stems === podium[1].stems ? "Joint lead, the race is on" : `${number(podium[0].stems - podium[1].stems)} stems clear of the chase`) : "Setting the pace for the team"}</span>
              </div>
              <div className="hv-hero-score"><strong className="hv-num">{number(podium[0].stems)}</strong><small>stems bunched</small></div>
            </div>

            <div className="hv-chasers">
              {podium.slice(1).map((person, i) => (
                <div key={person.id} data-place={i + 2} className={`hv-chaser ${clickable}`} {...detailProps(person)}>
                  <span className="hv-medal">{i + 2}</span>
                  <div className="hv-chaser-who">
                    <strong>{person.name}</strong>
                    <small>{person.stems === podium[0].stems ? "Level with the leader" : `${number(podium[0].stems - person.stems)} stems behind`}</small>
                  </div>
                  <b className="hv-num">{number(person.stems)}</b>
                </div>
              ))}
              {podium.length === 1 && <p className="hv-chase-wait">The chase starts with the next buncher.</p>}
            </div>

            <div className="hv-team">
              <p>Team progress</p>
              <div className="hv-ring" style={{ "--pct": Math.min(100, teamPercent || 0) }} role="progressbar" aria-label="Team target completion"
                aria-valuetext={teamPercent == null ? "Target not set" : `${teamPercent}% of team target`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={teamPercent == null ? undefined : Math.min(100, teamPercent)}>
                <strong className="hv-num">{teamPercent == null ? "—" : `${teamPercent}%`}</strong>
              </div>
              <small><b>{target ? finishers : "—"}</b> of {windowBuncherCount} at target</small>
              {teamPercent >= 100 && <small className="hv-goal">Team goal reached. Keep growing.</small>}
            </div>
          </section>
        )}

        {/* ===== metric tiles ===== */}
        <section className="hv-metrics" aria-label="Packhouse totals">
          <article className="hv-m hv-m-target"><h2>Window target</h2><strong>{number(selected?.packhouse_target)}</strong><p>{targetNote}</p></article>
          <article className="hv-m hv-m-within"><h2>Within window</h2><strong>{number(windowStems)}</strong><p>Counts toward target</p></article>
          <article className="hv-m hv-m-outside"><h2>Outside window</h2><strong>{number(selected?.out_of_window_stems)}</strong><p>Before start or after end</p></article>
          <button type="button" className="hv-m hv-m-total hv-m-click" onClick={() => setShowGradeout(true)} disabled={!selected} aria-haspopup="dialog" aria-label={`View full-day grade-out summary for ${selected?.name || "this farm"}`}>
            <h2>Full-day total</h2><strong>{number(selected?.total_stems)}</strong><p>All recorded stems · View varieties &amp; lengths</p>
          </button>
          <article className="hv-m hv-m-crew"><h2>Bunchers in line</h2><strong>{number(selected?.bunchers_in_window ?? selected?.in_line)}</strong><p>People with captures</p></article>
          <article className={`hv-m hv-m-clock ${windowActive ? "is-active" : ""} ${windowElapsed ? "is-complete" : ""}`}>
            <h2><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></svg>Bunching window</h2>
            <strong className="hv-num">{clock(windowRemaining)}</strong>
            <div className="hv-elapsed" aria-hidden="true"><i style={{ "--elapsed": elapsedPct }} /></div>
            <p><span>{windowElapsed ? "Window complete" : "7.67h capture"}</span><span>{windowStart && windowEnd ? `${hhmm(windowStart)} – ${hhmm(windowEnd)}` : "Awaiting start time"}</span></p>
          </article>
        </section>

        {/* ===== the rows ===== */}
        <section className="hv-lineup">
          <div className="hv-lineup-head">
            <div>
              <h2>{selected?.name || "Buncher performance"}</h2>
              <p className="hv-note">{windowStart ? "Targets are based on 7.67 productive capture hours. The start to end clock includes lunch and tea breaks; captures continue afterwards." : "Set a daily start time. Targets are based on 7.67 productive capture hours, with lunch and tea breaks included in the clock span."}</p>
            </div>
            <div className="hv-legend"><span>Stems bunched</span><span>Still to go</span></div>
          </div>

          {offline && !isHistorical && <p className="hv-notice" role="status">Updates are delayed. Showing the last successful figures.</p>}
          {!target && displayData && <p className="hv-notice">{isHistorical ? "No target was set for this day." : "Today’s target has not been set for this farm."} Actual stems are shown; target progress appears once an admin saves the target.</p>}

          {!displayData ? (
            <div className="hv-empty">{offline ? "Unable to load the live board. Retrying automatically…" : "Loading today’s performance…"}</div>
          ) : !rows.length ? (
            <div className="hv-empty">
              <h3>{isHistorical ? "No bunchers recorded for this day." : "Ready for the first bunch."}</h3>
              <p>{isHistorical ? "Try another day with the date picker." : "Bunchers appear here as soon as their first capture arrives."}</p>
            </div>
          ) : (
            <div className="hv-roster" style={{ "--roster-columns": fit.columns, "--roster-rows": Math.ceil(rows.length / fit.columns) }}>
              {rows.map(person => {
                const inWindow = person.has_window_capture ?? person.stems > 0;
                const achieved = Boolean(inWindow && target && person.stems >= target);
                const rank = windowRows.findIndex(r => r.id === person.id) + 1;
                const progress = !inWindow ? 0 : target ? Math.min(100, person.stems / target * 100) : person.stems / scale * 100;
                const within = person.stems_in_window === undefined ? person.stems : person.stems_in_window;
                const total = person.total_stems === undefined ? person.stems : person.total_stems;
                const label = `${person.name}: ${number(person.stems)} stems; ${inWindow ? "captured within the window" : "outside the window"}; ${person.remaining == null ? "target not set" : `${number(person.remaining)} remaining`}`;
                const gap = !inWindow ? <>Outside window</>
                  : !target ? <>No target</>
                  : achieved ? (person.stems > target ? <><b>+{number(person.stems - target)}</b> over target</> : <>Target met</>)
                  : <><b>{number(person.remaining)}</b> to go</>;
                return (
                  <article key={person.id} {...detailProps(person)} data-rank={rank || undefined} aria-label={label} title={label}
                    className={`hv-lane ${clickable} ${achieved ? "is-done" : ""} ${inWindow ? "" : "is-out"}`}>
                    <span className="hv-rank">{rank || "–"}</span>
                    <div className="hv-lane-main">
                      <div className="hv-lane-top">
                        <h3 className={person.name.length > 23 ? "is-long" : ""}>{person.name}</h3>
                        <span className="hv-score">{number(within)}</span>
                      </div>
                      <div className="hv-chips" aria-label={`${person.name}: ${number(within)} within window, ${number(person.stems_outside_window)} outside window, ${number(total)} total`}>
                        <span className="hv-chip hv-chip-out">Outside <b>{number(person.stems_outside_window)}</b></span>
                        <span className="hv-chip hv-chip-total">Total <b>{number(total)}</b></span>
                      </div>
                      <div className="hv-track-row">
                        <div className="hv-trackwrap">
                          <div className={`hv-track ${target ? "" : "is-none"}`}><i style={{ width: `${progress}%` }} /></div>
                          <span className="hv-bud" style={{ "--pct": `${progress}%` }}><Flower /></span>
                        </div>
                        <span className={`hv-gap ${achieved ? "is-done" : !inWindow || !target ? "is-muted" : ""}`}>{gap}</span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <footer className="hv-foot">
            <span>{target ? `Daily target: ${number(target)} stems per person` : "Daily targets are managed by your administrator"}</span>
            <span>{isHistorical ? "Loaded" : "Updated"} {displayData ? new Date(displayData.updated_at).toLocaleTimeString("en-GB", { timeZone: "Africa/Nairobi" }) : "—"}</span>
          </footer>
        </section>

        <p className="hv-signoff">Where people and quality count.</p>
      </div>

      {detail && <BuncherDetailDialog person={detail.person} farm={detail.farm} client={publicApi} onClose={closeDetail} />}
      {showGradeout && selected && <FarmGradeoutDialog farm={selected.name} day={displayData?.day || nairobiToday()} client={publicApi} onClose={() => setShowGradeout(false)} />}
    </main>
  );
}